import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ActionErrorDialog } from "@/components/shared/ActionErrorDialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { QueryCache, MutationCache } from "@tanstack/react-query";
import { handleQueryError } from "@/lib/permission-errors";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { ModuleThemeProvider } from "@/contexts/ModuleThemeContext";
import { UserStatusProvider } from "@/contexts/UserStatusContext";
import { isAppDomain } from "@/hooks/useDomainRedirect";
import { RabbitLoader } from "@/components/ui/rabbit-loader";

// -----------------------------------------------------------------------------
// Batch 5A (Q1) — Lazy-load pages
// -----------------------------------------------------------------------------
// Pages kept eager: Landing + Auth + RootRoute composition (critical first
// paint on marketing & login), NotFound (tiny + used by catch-all),
// AppLayout & SuperadminLayout (route wrappers — must resolve synchronously
// for the Suspense boundary to attach to child route elements), and GuestGuard
// (wraps almost every protected route, trivially small).
import Auth from "./pages/Auth";
import Landing from "./pages/Landing";
import NotFound from "./pages/NotFound";
import { GuestGuard } from "@/components/shared/GuestGuard";
import { AppLayout } from "./components/layout/AppLayout";
import { SuperadminLayout } from "./components/layout/SuperadminLayout";

// Lazy pages — each becomes its own JS chunk loaded on demand.
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const InvitationWelcome = lazy(() => import("./pages/InvitationWelcome"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const ProjectMembers = lazy(() => import("./pages/ProjectMembers"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const ProjectMilestones = lazy(() => import("./pages/ProjectMilestones"));
const Index = lazy(() => import("./pages/Index"));
const TermsOfUse = lazy(() => import("./pages/legal/TermsOfUse"));
const ITGuide = lazy(() => import("./pages/ITGuide"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const SharedProjectPage = lazy(() => import("./pages/SharedProjectPage"));
const ProjectInvitePage = lazy(() => import("./pages/ProjectInvitePage"));
const AccessControlPage = lazy(() => import("./pages/admin/AccessControlPage"));
const PermissionsGuide = lazy(() => import("./pages/PermissionsGuide"));
const TeamsPage = lazy(() => import("./pages/TeamsPage"));
const PlanningPage = lazy(() => import("./pages/PlanningPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const WikiPage = lazy(() => import("./pages/WikiPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AiPage = lazy(() => import("./pages/AiPage"));

// Superadmin pages — lazy (rarely accessed).
const SuperadminDashboard = lazy(() => import("./pages/superadmin/SuperadminDashboard"));
const SuperadminOrganizations = lazy(() => import("./pages/superadmin/SuperadminOrganizations"));
const SuperadminUsers = lazy(() => import("./pages/superadmin/SuperadminUsers"));
const SuperadminPlans = lazy(() => import("./pages/superadmin/SuperadminPlans"));
const SuperadminSubscriptions = lazy(() => import("./pages/superadmin/SuperadminSubscriptions"));
const SuperadminAdmins = lazy(() => import("./pages/superadmin/SuperadminAdmins"));
const SuperadminSettings = lazy(() => import("./pages/superadmin/SuperadminSettings"));
const SuperadminAuditLogs = lazy(() => import("./pages/superadmin/SuperadminAuditLogs"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
  // Intercepteur global : toute erreur de permission / session non gérée
  // par l'appelant déclenche automatiquement le popin explicatif.
  // Pour désactiver sur une mutation/query spécifique :
  //   useMutation({ ..., meta: { silent: true } })
  // Pour préciser le contexte (et donc le message affiché) :
  //   useMutation({ ..., meta: { actionContext: 'task.delete' } })
  queryCache: new QueryCache({
    onError: (err, query) => handleQueryError(err, query.meta),
  }),
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => handleQueryError(err, mutation.meta),
  }),
});

/**
 * Root route component that handles domain-based routing:
 * - app.lapyno.com / staging.app.lapyno.com:
 *   - If not logged in → Show Auth page
 *   - If logged in → Redirect to /app
 * - lapyno.com / www.lapyno.com → Show Landing page
 */
function RootRoute() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Marketing domain → show landing page
  if (!isAppDomain()) {
    return <Landing />;
  }
  
  // App domain - show loading while checking auth (seamless with marketing transition)
  if (loading) {
    return <RabbitLoader message="" />;
  }
  
  // App domain - not logged in → show Auth page with mode param
  if (!user) {
    const mode = searchParams.get('mode');
    return <Auth initialMode={mode === 'signup' ? 'signup' : 'login'} />;
  }
  
  // App domain - logged in → redirect to /app
  return <Navigate to="/app" replace />;
}

function LegacyProjectTimesheetRedirect() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  if (!id) {
    return <Navigate to="/projects" replace />;
  }

  const nextParams = new URLSearchParams(searchParams);
  nextParams.set('view', 'timesheet');

  return <Navigate to={`/projects/${id}?${nextParams.toString()}`} replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ActionErrorDialog />
      <BrowserRouter>
        <AuthProvider>
          <OrganizationProvider>
            <UserStatusProvider>
            <ModuleThemeProvider>
              {/* Batch 5A (Q1): Suspense boundary for lazy-loaded pages.
                  Falls back to RabbitLoader to match the loaders already used
                  in RootRoute and AppLayout, so transitions feel consistent. */}
              <Suspense fallback={<RabbitLoader message="" />}>
              <Routes>
                {/* Root route - Landing or redirect based on domain */}
                <Route path="/" element={<RootRoute />} />
                
                {/* Public routes */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                
                {/* Legal pages */}
                <Route path="/terms" element={<TermsOfUse />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/it-guide" element={<ITGuide />} />
                
                {/* Onboarding */}
                <Route path="/onboarding" element={<Onboarding />} />
                
                {/* Invitation Welcome */}
                <Route path="/invitation-welcome" element={<InvitationWelcome />} />
                
                {/* Access Denied */}
                <Route path="/access-denied" element={<AccessDenied />} />
                
                {/* Shared Project Links */}
                <Route path="/shared/:token" element={<SharedProjectPage />} />
                <Route path="/invite/:token" element={<ProjectInvitePage />} />
                
                {/* Protected routes with layout */}
                <Route element={<AppLayout />}>
                  {/* App entry point - redirect to dashboard */}
                  <Route path="/app" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<GuestGuard><DashboardPage /></GuestGuard>} />
                  
                  {/* Projects Module - Main entry point */}
                  <Route path="/projects" element={<Index />} />
                  <Route path="/projects/:id" element={<ProjectDetail />} />
                  <Route path="/projects/:id/timesheet" element={<LegacyProjectTimesheetRedirect />} />
                  <Route path="/projects/:id/milestones" element={<ProjectMilestones />} />
                  
                  <Route path="/members" element={<Navigate to="/admin/access?tab=members" replace />} />
                  {/* <Route path="/roles" element={<Roles />} /> */}
                  <Route path="/project-members" element={<ProjectMembers />} />
                  <Route path="/permissions-guide" element={<PermissionsGuide />} />
                  <Route path="/teams" element={<GuestGuard><TeamsPage /></GuestGuard>} />
                  <Route path="/teams/people" element={<GuestGuard><TeamsPage /></GuestGuard>} />
                  <Route path="/teams/analytics" element={<GuestGuard><TeamsPage /></GuestGuard>} />
                  <Route path="/teams/orgchart" element={<GuestGuard><TeamsPage /></GuestGuard>} />
                  <Route path="/planning" element={<GuestGuard><PlanningPage /></GuestGuard>} />
                  <Route path="/planning/workload" element={<GuestGuard><PlanningPage /></GuestGuard>} />
                  <Route path="/planning/heatmap" element={<GuestGuard><PlanningPage /></GuestGuard>} />
                  <Route path="/planning/gantt" element={<GuestGuard><PlanningPage /></GuestGuard>} />
                  <Route path="/planning/timeline" element={<GuestGuard><PlanningPage /></GuestGuard>} />
                  <Route path="/documents" element={<GuestGuard><DocumentsPage /></GuestGuard>} />
                  <Route path="/documents/:docId" element={<GuestGuard><DocumentsPage /></GuestGuard>} />
                  <Route path="/wiki" element={<GuestGuard><WikiPage /></GuestGuard>} />
                  <Route path="/wiki/:docId" element={<GuestGuard><WikiPage /></GuestGuard>} />
                  <Route path="/discussion" element={<GuestGuard><ChatPage /></GuestGuard>} />
                  <Route path="/discussion/:channelId" element={<GuestGuard><ChatPage /></GuestGuard>} />
                  <Route path="/ai" element={<GuestGuard><AiPage /></GuestGuard>} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/admin/access" element={<GuestGuard><AccessControlPage /></GuestGuard>} />
                  <Route path="/admin/settings" element={<GuestGuard><Settings /></GuestGuard>} />
                  <Route path="/admin/audit-logs" element={<GuestGuard><AuditLogs /></GuestGuard>} />
                </Route>
                
                {/* Superadmin Module - Separate Layout */}
                <Route element={<SuperadminLayout />}>
                  <Route path="/superadmin" element={<SuperadminDashboard />} />
                  <Route path="/superadmin/organizations" element={<SuperadminOrganizations />} />
                  <Route path="/superadmin/users" element={<SuperadminUsers />} />
                  <Route path="/superadmin/plans" element={<SuperadminPlans />} />
                  <Route path="/superadmin/subscriptions" element={<SuperadminSubscriptions />} />
                  <Route path="/superadmin/admins" element={<SuperadminAdmins />} />
                  <Route path="/superadmin/settings" element={<SuperadminSettings />} />
                  <Route path="/superadmin/audit-logs" element={<SuperadminAuditLogs />} />
                </Route>
                
                {/* Redirects - old routes to app entry point */}
                <Route path="/tasks" element={<Navigate to="/app" replace />} />
                
                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </ModuleThemeProvider>
            </UserStatusProvider>
          </OrganizationProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
